import React, { useMemo, useState } from 'react';
import { Box, Paper, Typography, Grid, Chip } from '@mui/material';
import Chart from 'react-apexcharts';

const WeeklyTrendsTab = ({ shipments = [] }) => {
  console.log(
    shipments.filter(ships => ships.shipmentStatus),
    'check ere status'
  );
  const [chartKey, setChartKey] = useState(0);

  // Calculate weekly data based on priority dates
  const weeklyData = useMemo(() => {
    if (!shipments.length) return [];

    // Function to get the Monday of a week for ANY date
    const getMondayOfWeek = date => {
      const d = new Date(date);
      const day = d.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday

      // Calculate days to subtract to get to Monday
      // If it's Sunday (0), subtract 6 days to get to previous Monday
      // If it's Monday (1), it's already Monday
      // Otherwise, subtract (day - 1) days
      const diff = day === 0 ? 6 : day - 1;

      d.setDate(d.getDate() - diff);
      d.setHours(0, 0, 0, 0);
      return d;
    };

    // Function to get the Friday of a week (from Monday)
    const getFridayOfWeek = mondayDate => {
      const friday = new Date(mondayDate);
      friday.setDate(friday.getDate() + 4); // Monday + 4 days = Friday
      friday.setHours(0, 0, 0, 0);
      return friday;
    };

    // Function to get the date for a shipment (with priority)
    const getShipmentDate = shipment => {
      // Priority 1: actualPickupDate
      if (shipment.actualPickupDate)
        return {
          date: shipment.actualPickupDate,
          type: 'pickup'
        };

      // Priority 2: shipmentDate
      if (shipment.shipmentDate)
        return {
          date: shipment.shipmentDate,
          type: 'shipment'
        };

      // Priority 3: actualDeliveryDate
      if (shipment.actualDeliveryDate)
        return {
          date: shipment.actualDeliveryDate,
          type: 'delivery'
        };

      return null;
    };

    const START_DATE = new Date('2025-08-01');
    const END_DATE = new Date();
    START_DATE.setHours(0, 0, 0, 0);
    END_DATE.setHours(0, 0, 0, 0);

    const weeklyGroups = {};
    const monthNames = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec'
    ];

    // Debug counters
    let totalShipments = 0;
    let includedShipments = 0;
    let dateSources = { pickup: 0, shipment: 0, delivery: 0, none: 0 };

    shipments.forEach(shipment => {
      totalShipments++;

      // Get date with priority order
      const dateInfo = getShipmentDate(shipment);
      if (!dateInfo) {
        dateSources.none++;
        return;
      }

      const { date: dateStr, type: dateType } = dateInfo;
      dateSources[dateType]++;

      const date = new Date(dateStr);
      if (isNaN(date)) return;

      // Get Monday and Friday for the week containing this date
      const monday = getMondayOfWeek(date);
      const friday = getFridayOfWeek(monday);

      // Filter by date range (Aug 2025 onwards)
      if (friday < START_DATE || friday > END_DATE) return;

      // Format for display
      const year = friday.getFullYear();
      const month = monthNames[friday.getMonth()];
      const day = friday.getDate().toString().padStart(2, '0');
      const displayKey = `${month}-${day}`; // e.g., "Sep-13"

      // Create key from Monday date
      const mondayKey = monday.toISOString().split('T')[0];

      if (!weeklyGroups[mondayKey]) {
        weeklyGroups[mondayKey] = {
          weekKey: displayKey, // Display: Sep-13
          mondayDate: monday,
          fridayDate: friday,
          fullDateKey: friday.toISOString().split('T')[0], // 2025-09-13
          month: month,
          year: year,
          freightSpend: 0,
          shipmentCount: 0,
          totalWeight: 0,
          // Track ALL dates by actual day
          byDay: {
            mon: 0,
            tue: 0,
            wed: 0,
            thu: 0,
            fri: 0,
            sat: 0,
            sun: 0 // Includes weekends
          },
          // Track date sources
          dateSources: { pickup: 0, shipment: 0, delivery: 0 }
        };
      }

      // Accumulate data
      weeklyGroups[mondayKey].freightSpend += Math.max(
        0,
        shipment.quoteNetCharge || 0
      );
      weeklyGroups[mondayKey].shipmentCount += 1;
      weeklyGroups[mondayKey].totalWeight += shipment.totalNetWeight || 0;
      weeklyGroups[mondayKey].dateSources[dateType]++;
      includedShipments++;

      // Track which day of the week the date occurred (ALL days)
      const dayOfWeek = date.getDay();
      switch (dayOfWeek) {
        case 0:
          weeklyGroups[mondayKey].byDay.sun++;
          break; // Sunday
        case 1:
          weeklyGroups[mondayKey].byDay.mon++;
          break;
        case 2:
          weeklyGroups[mondayKey].byDay.tue++;
          break;
        case 3:
          weeklyGroups[mondayKey].byDay.wed++;
          break;
        case 4:
          weeklyGroups[mondayKey].byDay.thu++;
          break;
        case 5:
          weeklyGroups[mondayKey].byDay.fri++;
          break;
        case 6:
          weeklyGroups[mondayKey].byDay.sat++;
          break; // Saturday
      }
    });

    // Convert to array and calculate derived metrics
    return Object.values(weeklyGroups)
      .map(week => ({
        ...week,
        costPerPound:
          week.totalWeight > 0 ? week.freightSpend / week.totalWeight : 0,
        averageShipmentCost:
          week.shipmentCount > 0 ? week.freightSpend / week.shipmentCount : 0,
        poundsMoved: week.totalWeight,
        // Add week range for tooltip
        weekRange: `Week ${week.mondayDate.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric'
        })} - ${week.fridayDate.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric'
        })}`,
        // Add day distribution
        dayDistribution: week.byDay,
        // Add date source info
        pickupCount: week.dateSources.pickup,
        shipmentDateCount: week.dateSources.shipment,
        deliveryCount: week.dateSources.delivery
      }))
      .sort((a, b) => a.mondayDate - b.mondayDate);
  }, [shipments]);

  const currentData = weeklyData;
  const xAxisLabels = currentData.map(w => w.weekKey);

  // Format functions for Y-axis labels
  const formatYAxisForFreightSpend = value => {
    if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
    return `$${Math.round(value)}`;
  };

  const formatYAxisForPounds = value => {
    if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `${(value / 1000).toFixed(0)}K`;
    return `${Math.round(value)}`;
  };

  const formatYAxisForDollars = (value, type) => {
    return `$${
      type === 'avgShipmentCost' ? value.toFixed(0) : value.toFixed(2)
    }`;
  };

  const formatYAxisForShipments = value => {
    if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
    return Math.round(value).toString();
  };

  const chartConfigs = [
    {
      id: 'freightSpend',
      title: 'Freight Spend',
      dataKey: 'freightSpend',
      color: '#8884d8',
      yAxisFormatter: formatYAxisForFreightSpend,
      tooltipFormatter: value => `$${Math.round(value).toLocaleString()}`
    },
    {
      id: 'shipmentCount',
      title: '# of Shipments',
      dataKey: 'shipmentCount',
      color: '#82ca9d',
      yAxisFormatter: formatYAxisForShipments,
      tooltipFormatter: value => Math.round(value).toString()
    },
    {
      id: 'costPerPound',
      title: 'Cost per pound',
      dataKey: 'costPerPound',
      color: '#ffc658',
      yAxisFormatter: value => formatYAxisForDollars(value),
      tooltipFormatter: value => `$${value.toFixed(2)}`
    },
    {
      id: 'poundsMoved',
      title: 'Pounds moved (lbs)',
      dataKey: 'poundsMoved',
      color: '#ff8042',
      yAxisFormatter: formatYAxisForPounds,
      tooltipFormatter: value => `${Math.round(value).toLocaleString()} lbs`
    },
    {
      id: 'averageShipmentCost',
      title: 'Average shipment cost',
      dataKey: 'averageShipmentCost',
      color: '#0088fe',
      yAxisFormatter: value => formatYAxisForDollars(value, 'avgShipmentCost'),
      tooltipFormatter: value => `$${Math.round(value).toLocaleString()}`
    }
  ];

  const getChartOptions = chart => ({
    chart: {
      type: 'line',
      height: 180,
      toolbar: { show: false }
    },
    colors: [chart.color],
    stroke: {
      curve: 'smooth',
      width: 4
    },
    markers: {
      size: 5
    },
    xaxis: {
      categories: xAxisLabels,
      labels: {
        rotate: -45,
        style: {
          fontSize: '12px'
        }
      }
    },
    yaxis: {
      labels: {
        formatter: chart.yAxisFormatter
      }
    },
    tooltip: {
      y: {
        formatter: chart.tooltipFormatter
      }
      // x: {
      //   formatter: (_, { dataPointIndex }) => {
      //     const week = currentData[dataPointIndex];
      //     let tooltip = `${week.weekKey}\n${week.weekRange}`;
      //     tooltip += `\n📊 Sources: Pickup(${week.pickupCount}) | Shipment(${week.shipmentDateCount}) | Delivery(${week.deliveryCount})`;
      //     return tooltip;
      //   }
      // }
    },
    grid: {
      padding: {
        top: 10,
        right: 10,
        bottom: 0,
        left: 10
      }
    }
  });

  return (
    <Box sx={{ width: '100%' }}>
      <Grid container spacing={0.5}>
        {chartConfigs.map(chart => (
          <Grid item xs={12} md={6} key={`${chart.id}-${chartKey}`}>
            <Paper sx={{ p: 1 }}>
              <Typography align='center' fontWeight='bold'>
                {chart.title}
              </Typography>
              <Chart
                options={getChartOptions(chart)}
                series={[
                  {
                    name: chart.title,
                    data: currentData.map(d => d[chart.dataKey])
                  }
                ]}
                type='line'
                height={180}
              />
            </Paper>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default WeeklyTrendsTab;
